import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import QuickbookConnectButton from '~/components/quickbook/quickbook-connect-button/QuickbookConnectButton';
import useQuickbook from '~/hooks/useQuickbook';
import { cast, handleApiError, notify } from '~/utils/helper';
import styles from './QuickBook.module.scss';
import classNames from 'classnames/bind';
import { useFormik } from 'formik';
import { IQuickbook } from '~/utils/interface/quickbook';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { handleGetQuickbookItems, handleUpdateQuickbook } from '~/thunks/quickbook/quickbookThunk';
import { useAppDispatch } from '~/redux/hooks';
import { SelectOption } from '~/utils/types/common';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);
const initialValues: IQuickbook = {};

const Quickbook = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const permissions = usePermission();
  const { t } = useTranslation(['admin/quickbook', 'admin/common']);
  const { quickbook, fetchQuickbook } = useQuickbook();
  const dispatch = useAppDispatch();
  const form = useFormik({
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(handleUpdateQuickbook(values)).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        handleEditItem(false);
        fetchQuickbook();
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isEditItem, setIsEditItem] = useState<boolean>(false);
  const [itemOptions, setItemOptions] = useState<Array<SelectOption>>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (quickbook) {
      form.setFieldValue('clientId', '');
      form.setFieldValue('clientSecret', '');
      form.setFieldValue('itemId', cast(quickbook.itemId));
      form.setFieldValue('isUpdateQuickbooksCustomer', quickbook?.isUpdateQuickbooksCustomer ?? false);
      form.setFieldValue('scopePayment', quickbook?.scopePayment ?? false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickbook]);

  useEffect(() => {
    if (quickbook?.isQuickbookConnected) {
      (async () => {
        try {
          const response = await dispatch(handleGetQuickbookItems()).unwrap();
          setItemOptions(response.responses as Array<SelectOption>);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickbook?.isQuickbookConnected]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleEditItem = (isEditItem: boolean, needRefreshData: boolean = false) => {
    setIsEditItem(isEditItem);
    if (!isEditItem) {
      form.resetForm();
    }
    if (needRefreshData) {
      fetchQuickbook();
    }
  };

  //#endregion Handle Function

  return (
    <>
      <BaseHeaderTitle className='mb-[20px]' title={t('quickbooks')} />
      <BaseRow className='gap-[20px] mb-[30px]'>
        <Card className='w-full'>
          <CardContent className='h-[200px] flex flex-col justify-between items-center'>
            <Typography variant='h5'>{t('quickbooksStatus')}</Typography>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className={cx('quickbook-status', { active: quickbook?.isQuickbookConnected })}>
                {t(`isQuickbookConnected.${cast(quickbook?.isQuickbookConnected)}`)}
              </Box>
            </BaseRow>
            <BaseRow>{permissions[PERMISSIONS.quickbooks_connect] && <QuickbookConnectButton />}</BaseRow>
          </CardContent>
        </Card>
        <Card className='w-full'>
          <CardContent className='h-[200px] flex flex-col justify-between items-center'>
            <Typography variant='h5'>{t('sync.title')}</Typography>
            <Box>
              {t('sync.syncAt')}:{' '}
              {quickbook?.syncAt ? dayjs(quickbook?.syncAt).format('MMM DD YYYY h:mm a') : t('sync.notSync')}
            </Box>
            <Box></Box>
          </CardContent>
        </Card>
      </BaseRow>
      <BaseRow>
        <Card className='w-full'>
          <CardContent className='flex flex-col justify-between'>
            <InputLabel>{t('items.title')}</InputLabel>
            <Box className='w-1/2 mb-[10px]'>
              <BaseSelect
                name='itemId'
                value={cast(form.values?.itemId)}
                options={itemOptions}
                onChange={form.handleChange}
                error={Boolean(form.errors?.itemId)}
                helperText={form.errors?.itemId}
                disabled={!isEditItem}
              ></BaseSelect>
            </Box>
            <Box className='mb-[10px]'>
              <FormControlLabel
                control={
                  <Checkbox checked={form.values?.isUpdateQuickbooksCustomer ?? false} onChange={form.handleChange} />
                }
                label={t('isUpdateQuickbooksCustomer')}
                name='isUpdateQuickbooksCustomer'
                value={true}
                disabled={!isEditItem}
              />
              <InputLabel>{t('isUpdateQuickbooksCustomerDescription')}</InputLabel>
            </Box>
            <Box className='mb-[10px]'>
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={true} />}
                  label={t('scopes.scopeAccounting')}
                  value={true}
                  disabled={true}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={form.values?.scopePayment ?? false} onChange={form.handleChange} />}
                  label={t('scopes.scopePayment')}
                  name='scopePayment'
                  value={true}
                  disabled={!isEditItem}
                />
              </Box>
            </Box>

            {permissions[PERMISSIONS.quickbooks_connect] && (
              <BaseRow className='w-full justify-end gap-[10px]'>
                {!isEditItem && (
                  <Button color='primary' variant='contained' onClick={() => handleEditItem(true)}>
                    {t('edit', { ns: 'admin/common' })}
                  </Button>
                )}
                {isEditItem && (
                  <>
                    <Button color='secondary' variant='contained' onClick={() => handleEditItem(false)}>
                      {t('cancel', { ns: 'admin/common' })}
                    </Button>
                    <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
                      {t('save', { ns: 'admin/common' })}
                    </Button>
                  </>
                )}
              </BaseRow>
            )}
          </CardContent>
        </Card>
      </BaseRow>
    </>
  );
};

export default Quickbook;
